// src/utils/ambassadorStatus.js
//
// Labels and colours for the ambassador app. A prospect's availability
// (backend utils/ambassador/availability.ts) drives its map colour; the
// four hues passed the dataviz palette validator (light surface, CVD-safe,
// >= 3:1 contrast); unavailable states share a neutral grey. Every colour
// is always shown with its text label (legend, detail chip) - never alone.

export const AVAILABILITY = {
  AVAILABLE: { label: 'Disponible', color: '#16a34a' },
  RESERVED_BY_ME: { label: 'Réservé pour moi', color: '#0b4dff' },
  FOLLOW_UP_MINE: { label: 'Ma relance', color: '#0b4dff' },
  RESERVED: { label: 'Réservé par un autre ambassadeur', color: '#c46200' },
  FOLLOW_UP: { label: 'En relance (autre ambassadeur)', color: '#c46200' },
  CUSTOMER: { label: 'Déjà client QRProofs', color: '#7c3aed' },
  COOLDOWN: { label: 'A refusé récemment', color: '#9ca3af' },
  DO_NOT_CONTACT: { label: 'Ne pas contacter', color: '#9ca3af' },
};

/** The map legend: one line per colour, in a fixed order. */
export const LEGEND = [
  ['#16a34a', 'Disponible'],
  ['#0b4dff', 'À moi (réservé ou relance)'],
  ['#c46200', 'Pris par un autre ambassadeur'],
  ['#7c3aed', 'Déjà client'],
  ['#9ca3af', 'Indisponible (refus récent, ne pas contacter)'],
];

export const VISIT_RESULTS = [
  ['OWNER_ABSENT', 'Gérant absent'],
  ['CALL_BACK', 'À rappeler / repasser'],
  ['INTERESTED', 'Intéressé'],
  ['INSTALLATION_ACCEPTED', 'Installation acceptée'],
  ['REFUSED', 'Refus'],
  ['CLOSED', 'Établissement fermé'],
  ['BAD_RECORD', 'Informations incorrectes'],
  ['OTHER', 'Autre'],
];
export const RESULT_LABEL = Object.fromEntries(VISIT_RESULTS);

export const FACILITY_TYPES = [
  ['restaurant', 'Restaurant'], ['bar', 'Bar'], ['cafe', 'Café'], ['hotel', 'Hôtel'],
  ['gite', 'Gîte / location'], ['camping', 'Camping'], ['shop', 'Commerce'], ['other', 'Autre'],
];
export const TYPE_LABEL = Object.fromEntries(FACILITY_TYPES);

/** "1:29:58" or "12:04" left until `until`; "expirée" once past. */
export function formatRemaining(until, now = Date.now()) {
  const ms = new Date(until).getTime() - now;
  if (!(ms > 0)) return 'expirée';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${Math.floor(s / 60)}:${ss}`;
}

/** "240 m" / "1,2 km" between two points (haversine). */
export function distanceLabel(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const rad = (v) => (v * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  const m = 2 * 6371000 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km`;
}

/** What an ambassador reads for an API error code. */
const ERRORS = {
  PROSPECT_ALREADY_RESERVED: 'Un autre ambassadeur vient de le réserver.',
  ALREADY_RESERVED_BY_YOU: 'Vous l’avez déjà réservé.',
  RESERVATION_LIMIT_REACHED: 'Vous avez déjà le nombre maximum de réservations en cours. Terminez ou annulez-en une.',
  PROSPECT_IN_FOLLOW_UP: 'Un autre ambassadeur suit cet établissement en ce moment.',
  PROSPECT_RECENTLY_REFUSED: 'Cet établissement a refusé récemment : pas de nouvelle visite pour l’instant.',
  PROSPECT_ALREADY_CUSTOMER: 'Cet établissement est déjà client QRProofs.',
  PROSPECT_DO_NOT_CONTACT: 'Cet établissement ne doit pas être contacté.',
  RESERVATION_REQUIRED: 'Réservez d’abord cet établissement.',
  NO_ACTIVE_RESERVATION: 'Plus de réservation en cours sur cet établissement.',
  EXTENSION_LIMIT_REACHED: 'Vous avez déjà prolongé cette réservation.',
  TOO_FAR_TO_EXTEND: 'Rapprochez-vous de l’établissement pour prolonger (moins de 500 m).',
  COMMENT_REQUIRED: 'Ajoutez un commentaire pour cette visite.',
  VISIT_TOO_OLD: 'Cette visite est trop ancienne pour donner un résultat.',
  RESULT_ALREADY_GIVEN: 'Le résultat de cette visite a déjà été donné.',
  PROSPECT_EXISTS: 'Cet établissement existe déjà (même SIRET).',
  POSITION_REQUIRED: 'Position introuvable : autorisez la localisation.',
  INVALID_SIRET: 'Le SIRET doit faire 14 chiffres.',
  NAME_REQUIRED: 'Le nom est obligatoire.',
  BBOX_TOO_LARGE: 'Zoomez pour voir les établissements.',
  PROSPECT_NOT_MINE: 'Réservez d’abord cet établissement pour le créer.',
  FACILITY_EXISTS: 'Cet établissement existe déjà sur QRProofs.',
  NO_AMBASSADOR_ACCOUNT: 'Votre compte n’est pas rattaché à un ambassadeur. Contactez QRProofs.',
};
export const errorText = (err) => ERRORS[err?.message] ?? 'Une erreur est survenue, réessayez.';
